// Key + settings storage (PRD 006 F2). Persisted in IndexedDB under a single
// record, with an in-memory cache so the synchronous-feeling read paths (token
// mint, Claude call) don't await a transaction every time. Keys are read only
// at the moment of use and never sent to a first-party origin (there is none).

import { openDb, wipeAll } from '@/lib/idb/idb'
import { DEFAULT_KEYS, type StoredKeys } from './types'

const RECORD_KEY = 'byok'

let cache: StoredKeys | null = null

/** Load keys + settings, hydrating the in-memory cache. */
export async function loadKeys(): Promise<StoredKeys> {
  if (cache) return cache
  const db = await openDb()
  const stored = await db.get<Partial<StoredKeys>>('keys', RECORD_KEY)
  cache = { ...DEFAULT_KEYS, ...stored }
  return cache
}

/** The cached keys if already loaded, else defaults. Synchronous, best-effort. */
export function peekKeys(): StoredKeys {
  return cache ?? DEFAULT_KEYS
}

/** Merge a patch into the stored keys and persist. Returns the new full record. */
export async function saveKeys(patch: Partial<StoredKeys>): Promise<StoredKeys> {
  const current = await loadKeys()
  const next: StoredKeys = { ...current, ...patch }
  cache = next
  const db = await openDb()
  await db.put('keys', next, RECORD_KEY)
  return next
}

/** Wipe keys, sessions, and graph from this browser (006 F8). */
export async function clearAllData(): Promise<void> {
  await wipeAll()
  cache = { ...DEFAULT_KEYS }
}
